import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  CalendarIcon,
  TicketIcon,
  CreditCardIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const navigation = [
  { name: 'nav.dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'nav.businesses', href: '/businesses', icon: BuildingOfficeIcon },
  { name: 'nav.services', href: '/services', icon: ClipboardDocumentListIcon },
  { name: 'nav.bookings', href: '/bookings', icon: CalendarIcon },
  { name: 'nav.events', href: '/events', icon: TicketIcon },
  { name: 'nav.subscription', href: '/subscription', icon: CreditCardIcon },
  { name: 'nav.profile', href: '/profile', icon: UserCircleIcon },
];

export function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const toggleLang = () => i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />
      )}

      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-secondary-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold text-primary-600">FitPulse</span>
          <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-lg text-secondary-600 hover:bg-secondary-100">
            {isOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <aside className={`fixed top-0 left-0 z-40 h-screen w-64 bg-white border-r border-secondary-200 transition-transform duration-300 ease-in-out lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center h-16 px-6 border-b border-secondary-200">
            <span className="text-xl font-bold text-primary-600">FitPulse</span>
          </div>

          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive ? 'bg-primary-50 text-primary-700' : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                  }`
                }
              >
                <item.icon className="h-5 w-5 mr-3" />
                {i18n.t(item.name)}
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-secondary-200 space-y-3">
            <button
              onClick={toggleLang}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-secondary-50 hover:bg-secondary-100 transition-colors text-sm font-medium text-secondary-600"
            >
              <span>🌐 {i18n.language === 'es' ? 'Español' : 'English'}</span>
              <span className="text-xs text-secondary-400">{i18n.language === 'es' ? 'EN →' : 'ES →'}</span>
            </button>
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-sm font-medium text-primary-700">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-secondary-900">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-secondary-500">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
